import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ProcedureSchedule } from '@/types/cgt'
import { procedureSchedulingApi } from '@/api/procedureSchedulingApi'
import type { BlockMove } from '@/lib/scheduling'

// ─── Query keys ────────────────────────────────────────────────────────────────

export const schedulingKeys = {
  upcoming: ['procedures', 'upcoming'] as const,
  resources: ['scheduling', 'resources'] as const,
  schedule: (id: string) => ['procedure-schedule', id] as const,
}

// ─── Queries ────────────────────────────────────────────────────────────────────

/** All upcoming (in-progress) procedures for the scheduler list. */
export function useUpcomingProcedures() {
  return useQuery({
    queryKey: schedulingKeys.upcoming,
    queryFn: () => procedureSchedulingApi.listUpcoming(),
  })
}

/** The bookable equipment + staff resources (calendar lanes). */
export function useResources() {
  return useQuery({
    queryKey: schedulingKeys.resources,
    queryFn: () => procedureSchedulingApi.listResources(),
  })
}

/** The computed schedule (blocks + conflicts) for one procedure. */
export function useProcedureSchedule(procedureId: string | undefined) {
  return useQuery({
    queryKey: schedulingKeys.schedule(procedureId ?? ''),
    queryFn: () => procedureSchedulingApi.getSchedule(procedureId as string),
    enabled: Boolean(procedureId),
  })
}

// ─── Mutation ─────────────────────────────────────────────────────────────────

export interface UpdateBlockVars {
  blockId: string
  move: Omit<BlockMove, 'blockId'>
}

/**
 * Reschedule / reassign a block via drag-and-drop. Writes the server-returned
 * schedule straight into the cache so conflict flags refresh immediately.
 */
export function useUpdateBlock(procedureId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ blockId, move }: UpdateBlockVars) =>
      procedureSchedulingApi.updateBlock(procedureId, blockId, move),
    onSuccess: (schedule: ProcedureSchedule) => {
      queryClient.setQueryData(schedulingKeys.schedule(procedureId), schedule)
    },
  })
}
